// src/components/Players/ScoutReports/scoutReportList.component.jsx
import React, { useEffect, useState } from "react";
import DragBox from "../../../Universals/DragBox/dragBox.component.jsx";
import { usePlayer } from "../../../../Contexts/playerData.context.jsx";

export default function ScoutReportList() {
  const API_ENDPOINT = "https://www.fbxchange.com/ReactPHP/scoutreports.php"; // unchanged

  const { activePlayer, setActiveReports } = usePlayer();

  const [user, setUser] = useState(null);
  const [reports, setReports] = useState([]);
  const [privateCount, setPrivateCount] = useState(0);
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [loading, setLoading] = useState(false); // NEW

  // width similar to notes list behavior
  const windowWidth = typeof window !== "undefined" ? window.innerWidth : 1600;
  const boxWidth = Math.min(1700, windowWidth - 730);

  // decode JWT (same style you used elsewhere)
  useEffect(() => {
    const token = localStorage.getItem("user2026");
    if (!token) return;
    try {
      const payload = JSON.parse(
        decodeURIComponent(
          atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
            .split("")
            .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
            .join("")
        )
      );
      setUser(payload);
    } catch (e) {
      console.error("Error decoding FBXUSER:", e);
    }
  }, []);

  // Fetch reports for the active player
  const fetchReports = async () => {
    if (!activePlayer?.FBXID || !user?.scout) {
      setReports([]);
      setPrivateCount(0);
      setLoading(false); // ensure loading ends if prerequisites missing
      return;
    }
    try {
      setLoading(true); // NEW
      const res = await fetch(API_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          scriptName: "getReports",
          FBXID: activePlayer.FBXID,
          scout: user.scout,
          team: user.team || "",
        }),
      });
      const json = await res.json().catch(() => ({}));
      const payload = json?.data || {};
      const list = Array.isArray(payload.reports) ? payload.reports : [];
      setActiveReports(list); // NEW: store in context
      setReports(list);
      const pCount = Number(payload.privateCount ?? 0);
      setPrivateCount(pCount);
    } catch (err) {
      console.error("fetchReports error:", err);
      setReports([]);
      setPrivateCount(0);
    } finally {
      setLoading(false); // NEW
    }
  };

  // Reset & load when player changes
  useEffect(() => {
    setReports([]);
    setPrivateCount(0);
    setSelectedReportId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePlayer?.FBXID]);

  // When we have both user + player, fetch
  useEffect(() => {
    if (user && activePlayer?.FBXID) {
      fetchReports();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, activePlayer?.FBXID]);

  const onRowClick = (report) => {
    setSelectedReportId((prev) => (String(prev) === String(report?.id) ? null : report?.id));
  };

  // Placeholder actions (wire up later if desired)
  const onCreateReport = () => {
    alert("Create Scout Report (coming soon)");
  };
  const onDeleteReport = (reportId) => {
    alert(`Delete report ${reportId} (coming soon)`);
  };
  const onTogglePrivate = (reportId) => {
    alert(`Toggle private ${reportId} (coming soon)`);
  };

  return (
    <div className="p-0 position-relative" style={{ position: "fixed", top: 0, right: 0, zIndex: 200 }}>
      <DragBox
        storageKey="SCOUT_REPORT_LIST_POSITION"
        defaultPos={{ x: boxWidth, y: 90 }}
        title={`SCOUT_REPORT ${activePlayer?.last ?? ""} ${activePlayer?.first ?? ""} (${reports.length} Reports, ${privateCount} Private)`}
        width={720}
        onAddFunction={onCreateReport}
        onAddText="New"
        addNote="Create New Scout Report"
      >
        {/* List area */}
        <div className="p-0" style={{ height: 176, overflowY: "auto", overflowX: "hidden" }}>
          {/* Sticky header */}
          <div
            className="d-flex bg-navy px-2 fs-7 fw-normal gap-2"
            style={{
              position: "sticky",
              top: 0,
              zIndex: 2,
              background: "var(--navy, #001f3f)",
              height: "35px",
              alignItems: "center",
            }}
          >
            <div className="col-6 text-lemon text-start">Actions</div>
            <div className="col-6 text-lemon">Date</div>
            <div className="col-14 text-lemon">Exposure</div>
            <div className="text-center col-3 text-lemon">Pos</div>
            <div className="text-center col-3 text-lemon">Fin</div>
            <div className="text-center col-3 text-lemon">%</div>
            <div className="col-5 text-lemon">By</div>
            <div className="text-center col-4 text-lemon">Team</div>
          </div>

          {loading ? (
            <div
              className="d-flex"
              style={{ height: "calc(100% - 35px)", alignItems: "center", justifyContent: "center" }}
            >
              <div className="alert alert-info w-100 text-center m-0 py-3 fw-bold" role="status">
                LOADING…
              </div>
            </div>
          ) : Array.isArray(reports) && reports.length > 0 ? (
            reports.map((rep, idx) => {
              const isPrivate = String(rep?.isPrivate).toUpperCase() === "Y";
              const author = String(rep?.SCOUT || "").trim();
              const me = String(user?.displayName || "").trim();
              const isMe = author && me && author === me;
              const isSelected = String(rep?.id) === String(selectedReportId);

              return (
                <div
                  key={rep?.id ?? idx}
                  className={`d-flex px-2 align-items-center gap-2 border-bottom fs-7 report-row${isSelected ? " report-selected" : ""
                    }`}
                  style={{ cursor: "pointer", height: "35px" }}
                >
                  <div className="col-6 p-0 d-flex gap-2">
                    <div className="text-truncate">{rep?.id || "Untitled"}</div>
                    {/* action buttons reserved for later */}
                    {/* <button
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => onDeleteReport(rep?.id)}
                      disabled={!isMe}
                      title="Delete Report"
                    >
                      Del
                    </button>
                    <button
                      className={`btn btn-sm ${isPrivate ? "btn-danger" : "btn-outline-danger"}`}
                      onClick={() => onTogglePrivate(rep?.id)}
                      disabled={!isMe}
                      title={isPrivate ? "Make Public" : "Make Private"}
                    >
                      P
                    </button> */}
                  </div>

                  <div className="col-6 text-start" onClick={() => onRowClick(rep)}>
                    {rep?.REPORT_DATE || ""}
                  </div>

                  <div
                    className={`col-14 text-truncate${isPrivate ? " text-danger fw-bold" : ""}`}
                    onClick={() => onRowClick(rep)}
                    title={rep?.exposure || ""}
                  >
                    {isPrivate && !isMe ? "Private Report" : rep?.exposure || ""}
                  </div>
                  <div className="col-3 text-center">{rep?.COL_POS1 || '-'}</div>
                  <div className="col-3 text-center">{rep?.FINAL || '-'}</div>
                  <div className="col-3 text-center">{rep?.Final_Calculated || '-'}</div>

                  <div className="col-5 text-start" onClick={() => onRowClick(rep)}>
                    {author || ""}
                  </div>

                  <div className="col-4 text-center" onClick={() => onRowClick(rep)}>
                    {rep?.VisibleTo || rep?.team || ""}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="d-flex py-2 border-bottom fs-7">
              <div className="col-30">No Scout Reports Found</div>
              <div className="col-10 text-center" />
              <div className="col-10 text-center" />
            </div>
          )}
        </div>
      </DragBox>

      {/* Hover/selection styles */}
      <style>{`
        .report-row:hover { background-color: #ffe7e7ff; }
        .report-selected { background-color: #d6e9ff; }
      `}</style>
    </div>
  );
}
